import React, { useState, useEffect } from 'react';
import { Trash2, RefreshCw, Database, AlertTriangle } from 'lucide-react';
import { 
  clearAllCache, 
  clearCacheCategory, 
  getCacheStatus, 
  formatBytes 
} from '../../services/cacheService';

const CacheManagement = ({ isAdmin }) => {
  const [cacheStatus, setCacheStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastCleared, setLastCleared] = useState(null);

  // Load cache status on component mount
  useEffect(() => {
    loadCacheStatus();
  }, []);

  const loadCacheStatus = () => {
    const status = getCacheStatus();
    setCacheStatus(status);
  };

  const handleClearAllCache = async () => {
    if (!window.confirm('⚠️ This will clear ALL cached data and force a reload from Supabase. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const result = clearAllCache();
      if (result.success) {
        setLastCleared({ type: 'all', count: result.clearedCount, time: new Date() });
        loadCacheStatus(); // Refresh status
        
        // Show success message
        alert(`✅ Successfully cleared ${result.clearedCount} cache items!`);
        
        // Suggest page reload
        if (window.confirm('Cache cleared! Reload the page to see fresh data from Supabase?')) {
          window.location.reload();
        }
      } else {
        alert('❌ Error clearing cache: ' + result.error.message);
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert('❌ Error clearing cache: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCategory = async (category) => {
    if (!window.confirm(`Clear all ${category} cache data?`)) {
      return;
    }

    setLoading(true);
    try {
      const result = clearCacheCategory(category);
      if (result.success) {
        setLastCleared({ type: category, count: result.clearedCount, time: new Date() });
        loadCacheStatus(); // Refresh status
        alert(`✅ Successfully cleared ${result.clearedCount} ${category} cache items!`);
      } else {
        alert('❌ Error clearing cache: ' + result.error.message);
      }
    } catch (error) {
      console.error('Error clearing category cache:', error);
      alert('❌ Error clearing cache: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl">
      <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6 text-orange-300" />
          <h2 className="text-xl font-bold text-orange-100">Cache Management</h2>
        </div>
        <p className="text-orange-200 text-sm mt-1">
          Manage localStorage cache and force data reload from Supabase
        </p>
      </div>

      <div className="p-6">
        {/* Warning Notice */}
        <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-yellow-200 font-bold text-sm">Cache Behavior</h3>
              <p className="text-yellow-300 text-sm mt-1">
                The app uses localStorage as a backup when Supabase is unavailable. If you delete data in Supabase but it still appears in the app, clear the cache to force a fresh reload.
              </p>
            </div>
          </div>
        </div>

        {/* Cache Status */}
        {cacheStatus && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-orange-300 font-bold">Cache Status</h3>
              <button
                onClick={loadCacheStatus}
                className="text-orange-400 hover:text-orange-300 transition-colors"
                title="Refresh cache status"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-gray-400 text-sm">Total Items:</span>
                  <span className="text-white font-bold ml-2">{cacheStatus.totalItems}</span>
                </div>
                <div>
                  <span className="text-gray-400 text-sm">Total Size:</span>
                  <span className="text-white font-bold ml-2">{formatBytes(cacheStatus.totalSize)}</span>
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="space-y-2">
                {Object.entries(cacheStatus.categories).map(([category, data]) => (
                  data.items > 0 && (
                    <div key={category} className="flex items-center justify-between bg-gray-600 rounded px-3 py-2">
                      <div>
                        <span className="text-orange-300 font-medium capitalize">{category}</span>
                        <span className="text-gray-400 text-sm ml-2">
                          ({data.items} items, {formatBytes(data.size)})
                        </span>
                      </div>
                      <button
                        onClick={() => handleClearCategory(category)}
                        disabled={loading}
                        className="bg-red-600 hover:bg-red-500 disabled:bg-gray-500 text-white px-2 py-1 rounded text-xs transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Clear All Button */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-orange-300 font-bold">Clear All Cache</h3>
            <p className="text-gray-400 text-sm">
              Remove all cached data and force reload from Supabase
            </p>
          </div>
          <button
            onClick={handleClearAllCache}
            disabled={loading}
            className="bg-red-600 hover:bg-red-500 disabled:bg-gray-500 text-white px-4 py-2 rounded font-bold transition-colors flex items-center gap-2"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            {loading ? 'Clearing...' : 'Clear All Cache'}
          </button>
        </div>

        {/* Last Cleared Info */}
        {lastCleared && (
          <div className="mt-4 p-3 bg-green-900 border border-green-600 rounded-lg">
            <p className="text-green-200 text-sm">
              ✅ Last cleared: {lastCleared.count} {lastCleared.type} cache items at {lastCleared.time.toLocaleTimeString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CacheManagement;
